import React, { useState, useEffect, useCallback } from 'react';
import { api, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Calculator, Pencil, Trash2, TrendingUp, TrendingDown, DollarSign, FileDown } from 'lucide-react';

const DEFAULTS = {
  extraction_rate: 80,
  packets_per_bale: 12,
  byproduct_revenue: 75000,
  cost_bailers_per_bale: 23,
  cost_packaging_paper_per_packet: 6.2,
  cost_packaging_labour_per_bale: 14,
};

const emptyForm = {
  name: '',
  factory: 'Both',
  pack_size: '2kg',
  raw_maize_weight_kg: '',
  extraction_rate: DEFAULTS.extraction_rate,
  actual_weight_per_packet: '',
  packets_per_bale: DEFAULTS.packets_per_bale,
  maize_cost_per_bag: '',
  maize_num_bags: '',
  total_maize_cost: '',
  selling_price_per_bale: '',
  byproduct_revenue: DEFAULTS.byproduct_revenue,
  byproduct_unit_price: '',
  byproduct_manual_override: false,
  cost_transport: 0,
  cost_commission: 0,
  cost_power: 0,
  cost_rental: 0,
  cost_bailers_per_bale: DEFAULTS.cost_bailers_per_bale,
  cost_packaging_paper_per_packet: DEFAULTS.cost_packaging_paper_per_packet,
  cost_packaging_labour_per_bale: DEFAULTS.cost_packaging_labour_per_bale,
  cost_operators: 0,
  cost_boys: 0,
  cost_offloading: 0,
  cost_misc: 0,
};

const fmt = (n) => Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtKES = (n) => `KES ${fmt(n)}`;

function localCalc(f) {
  const raw = parseFloat(f.raw_maize_weight_kg) || 0;
  const extPct = parseFloat(f.extraction_rate) || 80;
  const ext = extPct / 100;
  const byproductPct = Math.max(0, 100 - extPct);
  const byproductQty = raw * (byproductPct / 100);
  const wpp = parseFloat(f.actual_weight_per_packet) || 1;
  const ppb = parseInt(f.packets_per_bale) || 12;
  const flour = raw * ext;
  const packets = wpp > 0 ? flour / wpp : 0;
  const bales = ppb > 0 ? packets / ppb : 0;
  let maize = 0;
  if (parseFloat(f.total_maize_cost) > 0) maize = parseFloat(f.total_maize_cost);
  else if (parseFloat(f.maize_cost_per_bag) > 0 && parseFloat(f.maize_num_bags) > 0)
    maize = parseFloat(f.maize_cost_per_bag) * parseFloat(f.maize_num_bags);
  const bailersTotal = (parseFloat(f.cost_bailers_per_bale) || 0) * bales;
  const paperTotal = (parseFloat(f.cost_packaging_paper_per_packet) || 0) * packets;
  const labourTotal = (parseFloat(f.cost_packaging_labour_per_bale) || 0) * bales;
  const fixed = [f.cost_transport, f.cost_commission, f.cost_power, f.cost_rental, f.cost_operators, f.cost_boys, f.cost_offloading, f.cost_misc]
    .reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const varCosts = bailersTotal + paperTotal + labourTotal + fixed;
  const totalCost = maize + varCosts;
  const sp = parseFloat(f.selling_price_per_bale) || 0;
  // Byproduct revenue: manual override wins, else compute from qty × unit price, else stored value
  const unitPrice = parseFloat(f.byproduct_unit_price) || 0;
  let bp;
  if (f.byproduct_manual_override) {
    bp = parseFloat(f.byproduct_revenue) || 0;
  } else if (unitPrice > 0) {
    bp = byproductQty * unitPrice;
  } else {
    bp = parseFloat(f.byproduct_revenue) || 0;
  }
  const revBales = bales * sp;
  const totalRev = revBales + bp;
  const pl = totalRev - totalCost;
  const ppBale = bales > 0 ? pl / bales : 0;
  const breakeven = bales > 0 ? (totalCost - bp) / bales : 0;
  return { flour, packets, bales, maize, varCosts, totalCost, revBales, totalRev, pl, ppBale, breakeven, byproductPct, byproductQty, bpRevenue: bp };
}

export default function ProductionCosting() {
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [estimateDialog, setEstimateDialog] = useState(false);
  const [estimateTemplate, setEstimateTemplate] = useState(null);
  const [actualBales, setActualBales] = useState('');
  const [estimateResult, setEstimateResult] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/costing-templates');
      setTemplates(res.data);
    } catch { toast.error('Failed to load templates'); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const preview = localCalc(form);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name, factory: t.factory, pack_size: t.pack_size,
      raw_maize_weight_kg: t.raw_maize_weight_kg, extraction_rate: t.extraction_rate,
      actual_weight_per_packet: t.actual_weight_per_packet, packets_per_bale: t.packets_per_bale,
      maize_cost_per_bag: t.maize_cost_per_bag || '', maize_num_bags: t.maize_num_bags || '',
      total_maize_cost: t.total_maize_cost || '', selling_price_per_bale: t.selling_price_per_bale,
      byproduct_revenue: t.byproduct_revenue,
      byproduct_unit_price: t.byproduct_unit_price ?? '',
      byproduct_manual_override: !!t.byproduct_manual_override,
      cost_transport: t.cost_transport,
      cost_commission: t.cost_commission, cost_power: t.cost_power, cost_rental: t.cost_rental,
      cost_bailers_per_bale: t.cost_bailers_per_bale, cost_packaging_paper_per_packet: t.cost_packaging_paper_per_packet,
      cost_packaging_labour_per_bale: t.cost_packaging_labour_per_bale, cost_operators: t.cost_operators,
      cost_boys: t.cost_boys, cost_offloading: t.cost_offloading, cost_misc: t.cost_misc,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.raw_maize_weight_kg || !form.actual_weight_per_packet) {
      toast.error('Please fill required fields'); return;
    }
    const payload = {
      ...form,
      raw_maize_weight_kg: parseFloat(form.raw_maize_weight_kg) || 0,
      extraction_rate: parseFloat(form.extraction_rate) || 80,
      actual_weight_per_packet: parseFloat(form.actual_weight_per_packet) || 0,
      packets_per_bale: parseInt(form.packets_per_bale) || 12,
      maize_cost_per_bag: parseFloat(form.maize_cost_per_bag) || null,
      maize_num_bags: parseFloat(form.maize_num_bags) || null,
      total_maize_cost: parseFloat(form.total_maize_cost) || null,
      selling_price_per_bale: parseFloat(form.selling_price_per_bale) || 0,
      byproduct_revenue: parseFloat(form.byproduct_revenue) || 0,
      byproduct_unit_price: form.byproduct_unit_price === '' ? null : (parseFloat(form.byproduct_unit_price) || 0),
      byproduct_manual_override: !!form.byproduct_manual_override,
      cost_transport: parseFloat(form.cost_transport) || 0,
      cost_commission: parseFloat(form.cost_commission) || 0,
      cost_power: parseFloat(form.cost_power) || 0,
      cost_rental: parseFloat(form.cost_rental) || 0,
      cost_bailers_per_bale: parseFloat(form.cost_bailers_per_bale) || 0,
      cost_packaging_paper_per_packet: parseFloat(form.cost_packaging_paper_per_packet) || 0,
      cost_packaging_labour_per_bale: parseFloat(form.cost_packaging_labour_per_bale) || 0,
      cost_operators: parseFloat(form.cost_operators) || 0,
      cost_boys: parseFloat(form.cost_boys) || 0,
      cost_offloading: parseFloat(form.cost_offloading) || 0,
      cost_misc: parseFloat(form.cost_misc) || 0,
    };
    try {
      if (editingId) await api.put(`/costing-templates/${editingId}`, payload);
      else await api.post('/costing-templates', payload);
      toast.success(editingId ? 'Template updated' : 'Template created');
      setDialogOpen(false);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/costing-templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const openEstimate = (t) => {
    setEstimateTemplate(t);
    setActualBales('');
    setEstimateResult(null);
    setEstimateDialog(true);
  };

  const calcEstimate = async () => {
    if (!actualBales) return;
    try {
      const res = await api.post(`/costing-templates/${estimateTemplate.id}/estimate`, {
        actual_bales_sold: parseFloat(actualBales)
      });
      setEstimateResult(res.data);
    } catch { toast.error('Failed to calculate'); }
  };

  const downloadTemplatePDF = async (t) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/costing-templates/${t.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Costing_${(t.name || 'template').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1B4D3E]">Production Costing</h1>
          <p className="text-sm text-slate-500">Profit template analysis (does not affect accounting)</p>
        </div>
        <Button onClick={openCreate} className="btn-primary" data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-2" /> New Template
        </Button>
      </div>

      {/* Templates Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Factory</TableHead>
                <TableHead>Pack Size</TableHead>
                <TableHead className="text-right">Bales</TableHead>
                <TableHead className="text-right">Total Cost</TableHead>
                <TableHead className="text-right">Total Revenue</TableHead>
                <TableHead className="text-right">Profit/Loss</TableHead>
                <TableHead className="text-right">Break-even</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">No templates yet</TableCell></TableRow>
              ) : templates.map(t => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell><Badge variant="outline">{t.factory}</Badge></TableCell>
                  <TableCell>{t.pack_size}</TableCell>
                  <TableCell className="text-right">{fmt(t.bales_produced)}</TableCell>
                  <TableCell className="text-right">{fmtKES(t.total_production_cost)}</TableCell>
                  <TableCell className="text-right">{fmtKES(t.total_revenue)}</TableCell>
                  <TableCell className="text-right">
                    <span className={t.profit_loss >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                      {fmtKES(t.profit_loss)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">{fmtKES(t.breakeven_price_per_bale)}/bale</TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEstimate(t)} className="text-green-600" title="Monthly Estimate">
                        <Calculator className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => downloadTemplatePDF(t)} className="text-slate-700" title="Download PDF" data-testid={`download-template-pdf-${t.id}`}>
                        <FileDown className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="text-blue-500">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Template' : 'New Costing Template'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Setup Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-700 border-b pb-1">Setup</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="col-span-2 sm:col-span-1 space-y-1">
                  <Label>Template Name *</Label>
                  <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Bisil 2kg" data-testid="template-name" />
                </div>
                <div className="space-y-1">
                  <Label>Factory</Label>
                  <Select value={form.factory} onValueChange={v => setField('factory', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                      <SelectItem value="Githunguri">Githunguri</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Pack Size</Label>
                  <Select value={form.pack_size} onValueChange={v => {
                    setField('pack_size', v);
                    if (v === '2kg') setField('cost_packaging_paper_per_packet', 6.2);
                    else setField('cost_packaging_paper_per_packet', 4.5);
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5kg">0.5kg</SelectItem>
                      <SelectItem value="1kg">1kg</SelectItem>
                      <SelectItem value="2kg">2kg</SelectItem>
                      <SelectItem value="50kg">50kg</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label>Raw Maize (kg) *</Label>
                  <Input type="number" value={form.raw_maize_weight_kg} onChange={e => setField('raw_maize_weight_kg', e.target.value)} data-testid="raw-maize-kg" />
                </div>
                <div className="space-y-1">
                  <Label>Extraction Rate %</Label>
                  <Input type="number" value={form.extraction_rate} onChange={e => setField('extraction_rate', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Weight/Packet (kg) *</Label>
                  <Input type="number" step="0.01" value={form.actual_weight_per_packet} onChange={e => setField('actual_weight_per_packet', e.target.value)} data-testid="weight-per-packet" />
                </div>
                <div className="space-y-1">
                  <Label>Packets/Bale</Label>
                  <Input type="number" value={form.packets_per_bale} onChange={e => setField('packets_per_bale', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Maize Cost Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-700 border-b pb-1">Maize Cost</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Cost/Bag</Label>
                  <Input type="number" value={form.maize_cost_per_bag} onChange={e => { setField('maize_cost_per_bag', e.target.value); setField('total_maize_cost', ''); }} placeholder="0" />
                </div>
                <div className="space-y-1">
                  <Label>No. Bags</Label>
                  <Input type="number" value={form.maize_num_bags} onChange={e => { setField('maize_num_bags', e.target.value); setField('total_maize_cost', ''); }} placeholder="0" />
                </div>
                <div className="space-y-1">
                  <Label>OR Total Maize Cost</Label>
                  <Input type="number" value={form.total_maize_cost} onChange={e => { setField('total_maize_cost', e.target.value); setField('maize_cost_per_bag', ''); setField('maize_num_bags', ''); }} placeholder="0" />
                </div>
              </div>
            </div>

            {/* Revenue Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-700 border-b pb-1">Revenue</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Selling Price/Bale (KES)</Label>
                  <Input type="number" value={form.selling_price_per_bale} onChange={e => setField('selling_price_per_bale', e.target.value)} data-testid="selling-price" />
                </div>
                <div className="space-y-1">
                  <Label>Byproduct % <span className="text-slate-400 text-[10px]">(auto = 100% − extraction)</span></Label>
                  <Input value={Number(localCalc(form).byproductPct).toFixed(2) + '%'} readOnly className="bg-slate-50" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Byproduct Qty (kg) <span className="text-slate-400 text-[10px]">(auto)</span></Label>
                  <Input value={fmt(localCalc(form).byproductQty)} readOnly className="bg-slate-50" />
                </div>
                <div className="space-y-1">
                  <Label>Byproduct Unit Price / kg</Label>
                  <Input type="number" step="0.01" value={form.byproduct_unit_price} onChange={e => { setField('byproduct_unit_price', e.target.value); setField('byproduct_manual_override', false); }} placeholder="0" data-testid="byproduct-unit-price" />
                </div>
                <div className="space-y-1">
                  <Label className="flex items-center justify-between">
                    <span>Total Byproduct Revenue</span>
                    <label className="text-[10px] font-normal text-slate-500 flex items-center gap-1">
                      <input type="checkbox" checked={!!form.byproduct_manual_override} onChange={e => setField('byproduct_manual_override', e.target.checked)} data-testid="byproduct-override-toggle" />
                      Override
                    </label>
                  </Label>
                  <Input
                    type="number"
                    value={form.byproduct_manual_override ? form.byproduct_revenue : Number(localCalc(form).bpRevenue).toFixed(2)}
                    onChange={e => setField('byproduct_revenue', e.target.value)}
                    disabled={!form.byproduct_manual_override}
                    className={form.byproduct_manual_override ? '' : 'bg-slate-50'}
                    data-testid="byproduct-revenue"
                  />
                </div>
              </div>
            </div>

            {/* Cost Inputs Section */}
            <div className="space-y-3">
              <h3 className="font-semibold text-slate-700 border-b pb-1">Cost Inputs</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ['Transport', 'cost_transport'],
                  ['Commission', 'cost_commission'],
                  ['Power', 'cost_power'],
                  ['Rental', 'cost_rental'],
                  ['Operators', 'cost_operators'],
                  ['Squad', 'cost_boys'],
                  ['Offloading', 'cost_offloading'],
                  ['Misc', 'cost_misc'],
                ].map(([label, key]) => (
                  <div key={key} className="space-y-1">
                    <Label className="text-xs">{label}</Label>
                    <Input type="number" value={form[key]} onChange={e => setField(key, e.target.value)} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Bailers/Bale</Label>
                  <Input type="number" value={form.cost_bailers_per_bale} onChange={e => setField('cost_bailers_per_bale', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Packaging Paper/Packet</Label>
                  <Input type="number" step="0.1" value={form.cost_packaging_paper_per_packet} onChange={e => setField('cost_packaging_paper_per_packet', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Packaging Labour/Bale</Label>
                  <Input type="number" value={form.cost_packaging_labour_per_bale} onChange={e => setField('cost_packaging_labour_per_bale', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2 border">
              <h3 className="font-semibold text-slate-700">Live Preview</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm">
                <div>Flour Output: <span className="font-medium">{fmt(preview.flour)} kg</span></div>
                <div>Packets: <span className="font-medium">{fmt(preview.packets)}</span></div>
                <div>Bales: <span className="font-bold text-[#1B4D3E]" data-testid="preview-bales">{fmt(preview.bales)}</span></div>
                <div>Maize Cost: <span className="font-medium">{fmtKES(preview.maize)}</span></div>
                <div>Variable Costs: <span className="font-medium">{fmtKES(preview.varCosts)}</span></div>
                <div>Total Cost: <span className="font-bold text-red-600">{fmtKES(preview.totalCost)}</span></div>
                <div>Bale Revenue: <span className="font-medium">{fmtKES(preview.revBales)}</span></div>
                <div>Total Revenue: <span className="font-bold text-blue-600">{fmtKES(preview.totalRev)}</span></div>
                <div>
                  Profit/Loss: <span className={`font-bold ${preview.pl >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="preview-profit">
                    {fmtKES(preview.pl)}
                  </span>
                </div>
                <div>Profit/Bale: <span className="font-medium">{fmtKES(preview.ppBale)}</span></div>
                <div>Break-even: <span className="font-bold">{fmtKES(preview.breakeven)}/bale</span></div>
              </div>
            </div>

            <Button type="submit" className="w-full btn-primary" data-testid="save-template-btn">
              {editingId ? 'Update Template' : 'Save Template'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Monthly Estimate Dialog */}
      <Dialog open={estimateDialog} onOpenChange={setEstimateDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Monthly Estimate — {estimateTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-500">
              Template produces <span className="font-bold text-[#1B4D3E]">{fmt(estimateTemplate?.bales_produced)}</span> bales/run at <span className="font-bold">{fmtKES(estimateTemplate?.selling_price_per_bale)}</span>/bale
            </div>
            <div className="space-y-2">
              <Label>Actual Bales Sold</Label>
              <div className="flex gap-2">
                <Input type="number" value={actualBales} onChange={e => setActualBales(e.target.value)} placeholder="Enter bales sold" data-testid="actual-bales-input" />
                <Button onClick={calcEstimate} className="btn-primary" data-testid="calc-estimate-btn">
                  <Calculator className="h-4 w-4 mr-1" /> Calculate
                </Button>
              </div>
            </div>
            {estimateResult && (
              <div className="space-y-3 bg-slate-50 rounded-lg p-4 border">
                {/* Production runs breakdown */}
                <div className="text-xs text-slate-600 border-b pb-2 space-y-1">
                  <div className="flex justify-between">
                    <span>Production Runs Needed</span>
                    <span className="font-medium text-slate-800" data-testid="estimate-runs">{fmt(estimateResult.production_runs)} runs</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bales per Run</span>
                    <span className="font-medium text-slate-800">{fmt(estimateResult.bales_per_run)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Packets</span>
                    <span className="font-medium text-slate-800">{fmt(estimateResult.actual_packets)}</span>
                  </div>
                </div>

                {/* Cost breakdown */}
                <div className="text-xs space-y-1 border-b pb-2">
                  <div className="font-semibold text-slate-700 mb-1">Cost Breakdown</div>
                  <div className="flex justify-between text-slate-600">
                    <span>Fixed Costs (scaled by runs)</span>
                    <span className="font-medium" data-testid="estimate-fixed">{fmtKES(estimateResult.total_fixed_costs)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Variable Costs (per bale/packet)</span>
                    <span className="font-medium" data-testid="estimate-variable">{fmtKES(estimateResult.total_variable_costs)}</span>
                  </div>
                </div>

                {/* Revenue breakdown */}
                <div className="text-xs space-y-1 border-b pb-2">
                  <div className="font-semibold text-slate-700 mb-1">Revenue Breakdown</div>
                  <div className="flex justify-between text-slate-600">
                    <span>Bales Revenue</span>
                    <span className="font-medium">{fmtKES(estimateResult.bales_revenue)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Byproduct Revenue (×{fmt(estimateResult.production_runs)} runs)</span>
                    <span className="font-medium">{fmtKES(estimateResult.total_byproduct_revenue)}</span>
                  </div>
                </div>

                <div className="text-xs text-slate-500 pb-1">
                  Cost/Bale: <span className="font-medium text-slate-700">{fmtKES(estimateResult.cost_per_bale)}</span> | Selling Price/Bale: <span className="font-medium text-slate-700">{fmtKES(estimateResult.selling_price_per_bale)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Estimated Revenue</span>
                  <span className="font-bold text-blue-600">{fmtKES(estimateResult.estimated_revenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Estimated Cost</span>
                  <span className="font-bold text-red-600">{fmtKES(estimateResult.estimated_cost)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-slate-600">Estimated Profit/Loss</span>
                  <span className={`font-bold ${estimateResult.estimated_profit_loss >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="estimate-profit">
                    {estimateResult.estimated_profit_loss >= 0 ? <TrendingUp className="h-4 w-4 inline mr-1" /> : <TrendingDown className="h-4 w-4 inline mr-1" />}
                    {fmtKES(estimateResult.estimated_profit_loss)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Profit/Bale</span>
                  <span className="font-bold">{fmtKES(estimateResult.estimated_profit_per_bale)}</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
